import React, { useState, useEffect } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { USER_TYPES } from '../../constants/userTypes';
import GuardianMarketplace from './guardian/GuardianMarketplace';
import ProviderMarketplace from './provider/ProviderMarketplace';
import { marketplaceAPI } from '../../services/marketplaceAPI';

/**
 * Marketplace Container Component
 * 
 * Functions as the main entry point and "Controller" for the Marketplace feature.
 * Determines which view to show based on user role.
 */
const MarketplaceContainer = () => {
    const { user } = useAuth();
    const [cartItems, setCartItems] = useState([]);
    const [isCartOpen, setIsCartOpen] = useState(false);

    // Determine view based on user type
    // Default to Guardian view if unknown, but specialized view for Providers
    const isProvider = user?.user_type === USER_TYPES.SERVICE_PROVIDER ||
        user?.user_type === 'PET_SHOP_OWNER' ||
        user?.user_type === 'pet_shop_owner';

    const userType = isProvider ? 'PROVIDER' : 'GUARDIAN';

    // --- Cart Actions (Guardian Only) ---
    const addToCart = (product) => {
        setCartItems(prev => {
            const existing = prev.find(item => item.id === product.id);
            if (existing) {
                return prev.map(item =>
                    item.id === product.id ? { ...item, quantity: item.quantity + 1 } : item
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
        setIsCartOpen(true); // Open cart to show feedback
    };

    const removeFromCart = (productId) => {
        setCartItems(prev => prev.filter(item => item.id !== productId));
    };

    const updateQuantity = (productId, delta) => {
        setCartItems(prev => prev.map(item => {
            if (item.id === productId) {
                const newQty = Math.max(1, item.quantity + delta);
                return { ...item, quantity: newQty };
            }
            return item;
        }));
    };

    if (!user) {
        return (
            <div className="flex items-center justify-center min-h-[50vh]">
                <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary-600"></div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gray-50 pb-20">
            {/* 
        We pass common props down.
        In a real app, we might use a Context, but props are fine for this depth.
      */}
            {isProvider ? (
                <ProviderMarketplace
                    user={user}
                />
            ) : (
                <GuardianMarketplace
                    user={user}
                    cartItems={cartItems}
                    isCartOpen={isCartOpen}
                    setIsCartOpen={setIsCartOpen}
                    onAddToCart={addToCart}
                    onRemoveFromCart={removeFromCart}
                    onUpdateQuantity={updateQuantity}
                />
            )}
        </div>
    );
};

export default MarketplaceContainer;
